import React, { useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { Store, Building2, Rocket, Landmark, ArrowRight } from 'lucide-react';

const PentestTarget = () => {
  const targets = [
    {
      title: "PME",
      icon: <Store size={20} />,
      desc: "Entreprises souhaitant identifier les vulnérabilités de leurs systèmes avant qu'elles ne soient exploitées.",
    },
    {
      title: "Grandes entreprises",
      icon: <Building2 size={20} />,
      desc: "Organisations disposant d'infrastructures complexes nécessitant des audits de conformité réguliers.",
    },
    {
      title: "Startups Tech",
      icon: <Rocket size={20} />,
      desc: "Produits numériques innovants nécessitant des tests de sécurité approfondis avant chaque mise en production.",
    },
    {
      title: "Institutions",
      icon: <Landmark size={20} />,
      desc: "Organismes publics ou sensibles souhaitant renforcer leur posture de sécurité et protéger les données citoyennes.",
    },
  ];

  const lineControls = useAnimation();

  useEffect(() => {
    // Cartoon bounce: shoot left→right, overshoot, snap back
    lineControls.start({
      x: ["-220px", "-220px", "180px", "-18px", "8px", "0px"],
      opacity: [0, 1, 1, 1, 1, 1],
      transition: {
        duration: 1.1,
        delay: 0.2,
        times: [0, 0.05, 0.55, 0.72, 0.84, 1],
        ease: "easeOut",
      },
    });
  }, []);

  return (
    // ✅ Full-bleed: no horizontal padding on section, no max-width
    <section className="w-full bg-[#f8fafc] py-18 font-inter">
      <div className="px-12 text-center">

        {/* HEADER */}
        <div className="mb-14">
          <span className="text-[#3b82f6] text-[11px] font-black uppercase tracking-[0.2em] block mb-3">
            Expertise Cyber
          </span>

          <h2 className="text-[#0f172a] text-[32px] lg:text-[40px] font-[900] mb-3 tracking-tight">
            À qui s'adressent nos pentests ?
          </h2>

          {/* ✅ Cartoon line animation */}
          <div className="flex justify-center overflow-hidden mb-7">
            <motion.div
              initial={{ x: "-220px", opacity: 0 }}
              animate={lineControls}
              className="h-1.5 w-16 bg-[#3b82f6] rounded-full"
            />
          </div>

          <p className="text-slate-400 text-[15px] max-w-[600px] mx-auto font-medium leading-relaxed">
            Nous accompagnons une diversité d'acteurs pour sécuriser leurs actifs numériques grâce à des audits de sécurité rigoureux.
          </p>
        </div>

        {/* CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {targets.map((item, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              whileHover={{ y: -8 }}
              className="group bg-white p-8 rounded-2xl border border-slate-100 shadow-sm flex flex-col items-start text-left transition-all duration-300 hover:shadow-xl hover:border-blue-100"
            >
              <div className="p-3 rounded-xl bg-[#E0F2FE] text-[#3b82f6] mb-6 transition-colors duration-300 group-hover:bg-[#3b82f6] group-hover:text-white">
                {item.icon}
              </div>
              <h3 className="text-[#0f172a] text-[18px] font-black mb-3">
                {item.title}
              </h3>
              <p className="text-slate-400 text-[13px] leading-relaxed mb-8 font-medium flex-1">
                {item.desc}
              </p>
              <button className="mt-auto flex items-center gap-2 text-[#3b82f6] text-[11px] font-black uppercase tracking-wider group-hover:gap-3 transition-all duration-300">
                En savoir plus <ArrowRight size={14} />
              </button>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default PentestTarget;